/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Tooltip } from 'react-tooltip';
import { SkillBehaviorDropdown } from '../../../../components/skillBehaviorDropdown/SkillBehaviorDropdown';
import { CoachingAction } from '../../../../components/skillBehaviorDropdown/types';
import { metricValueFormat } from '../../../../utils/metricValueFormat';
import DescriptionBox from '../../../../components/descriptionBox/DescriptionBox';
import { CoachingTabs } from '../../../../utils/typeConstants';
import { OverridePerformancePopup } from '../../../../components/coachingActionPlan/overridePerformancePopup/OverridePerformancePopup';
import { METRIC_TYPE_CURRENCY, METRIC_TYPE_DECIMAL, METRIC_TYPE_NUMERIC, METRIC_TYPE_PERCENTAGE, METRIC_TYPE_TIME } from '../../coachingActionPlanTypes';
import editIcon from '../../../../assets/editIcon.png';

interface LayoutSectionProps {
    metricName: string;
    metricCurrentValue: number | null;
    metricTypeId?: number;
    metricTypeName?: string;
    performanceCommitment: string;
    onPerformanceCommitmentChange: (value: string) => void;
    followUpDate: Date | null;
    onFollowUpDateChange: (value: Date | null) => void;
    skillId: number | null;
    onSkillChange: (value: number) => void;
    behaviorId: number | null;
    onBehaviorChange: (value: number) => void;
    actionId: number | null;
    onActionChange: (value: number | null) => void;
    actionDescription: string;
    onActionDescriptionChange: (value: string) => void;
    successCriteria: string;
    onSuccessCriteriaChange: (value: string) => void;
    notes: string;
    onNotesChange: (value: string) => void;
    coachingActions: CoachingAction[];
    showValidation: boolean;
    CAPCurrentSession: any
    onBeforeOverride?: () => void;
}

export const LayoutSection: React.FC<LayoutSectionProps> = ({
    metricName,
    metricCurrentValue,
    metricTypeId,
    metricTypeName,
    performanceCommitment,
    onPerformanceCommitmentChange,
    followUpDate,
    onFollowUpDateChange,
    skillId,
    onSkillChange,
    behaviorId,
    onBehaviorChange,
    actionId,
    onActionChange,
    actionDescription,
    onActionDescriptionChange,
    successCriteria,
    onSuccessCriteriaChange,
    notes,
    onNotesChange,
    coachingActions,
    showValidation,
    CAPCurrentSession,
    onBeforeOverride,
}) => {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 1);
    const [selectedAction, setSelectedAction] = useState<CoachingAction | null>(null);
    const [showOverride, setShowOverride] = useState<boolean>(false);

    useEffect(() => {
        if (coachingActions?.length > 0) {
            const action = coachingActions.find((a) => a.coaching_action_id === actionId);
            setSelectedAction(action || null);
        } else {
            setSelectedAction(null);
        }
    }, [actionId, coachingActions]);
    // ── Per-type input config ────────────────────────────────────────────────
    const getTypeConfig = () => {
        switch (metricTypeId) {
            case METRIC_TYPE_PERCENTAGE:
                // Allows any value including negative — no upper/lower bound
                return {
                    regex: /^-?\d{0,3}(\.\d{0,2})?$/,
                    placeholder: '0.00',
                    suffix: '%',
                    hint: 'Enter a percentage value',
                };
            case METRIC_TYPE_CURRENCY:
                return {
                    regex: /^-?\d*(\.\d{0,2})?$/,
                    placeholder: '0.00',
                    suffix: '$',
                    hint: 'Enter a currency value (up to 2 decimal places)',
                };
            case METRIC_TYPE_NUMERIC:
                return {
                    regex: /^-?\d*$/,
                    placeholder: '0',
                    suffix: '',
                    hint: 'Enter a whole number',
                };
            case METRIC_TYPE_DECIMAL:
                return {
                    regex: /^-?\d*(\.\d{0,4})?$/,
                    placeholder: '0.0000',
                    suffix: '',
                    hint: 'Enter a decimal value (up to 4 decimal places)',
                };
            case METRIC_TYPE_TIME:
                return {
                    regex: /^-?\d*(\.\d{0,2})?$/,
                    placeholder: '0.00',
                    suffix: 's',
                    hint: 'Enter time in seconds (e.g. 120.50)',
                };
            default:
                return {
                    regex: /^-?\d*(\.\d{0,4})?$/,
                    placeholder: '0.00',
                    suffix: '',
                    hint: metricTypeName ? `Type: ${metricTypeName}` : 'Data type is defined by metric.',
                };
        }
    };

    const typeConfig = getTypeConfig();

    const performanceCommitmentKeyFilter = metricTypeId === METRIC_TYPE_NUMERIC
        ? /^-?\d*$/
        : /^-?\d*(\.\d*)?$/;

    const handlePerformanceCommitmentChange = (value: string) => {
        if (value === '' || typeConfig.regex.test(value)) {
            onPerformanceCommitmentChange(value);
        }
    };

    // Convert HTML to plain text (from CoachingToDo pattern)
    const convertHtmlToPlainText = (html: string): string => {
        if (!html) return '';
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const listItems = tempDiv.querySelectorAll('li');
        const formattedList: string[] = [];
        listItems.forEach((li) => {
            const textContent = li.textContent?.trim() ?? '';
            formattedList.push(`• ${textContent}`);
        });
        return formattedList.join('\n').trim();
    };

    // Handle action selection and auto-populate description
    const handleActionSelect = (action: CoachingAction | null) => {
        setSelectedAction(action);
        onActionChange(action?.coaching_action_id ?? null);
        if (action) {
            const actionDesc = convertHtmlToPlainText(action.coaching_action_description ?? '');
            const fullDescription = `${action.coaching_action_name.trim()} : \n${actionDesc.trim()}`;
            onActionDescriptionChange(fullDescription.replace(/\n/g, '\r\n'));
        } else {
            onActionDescriptionChange('');
        }
    };

    const coachingActionsTemplate = (option: CoachingAction) => (
        <div>
            <div>{option.coaching_action_name}</div>
            <div
                style={{
                    whiteSpace: 'pre-line',
                    width: '250px',
                    fontSize: '12px',
                    wordWrap: 'break-word',
                    fontWeight: '400',
                }}
                dangerouslySetInnerHTML={{ __html: option.coaching_action_description }}
            />
        </div>
    );

    return (
        <div className="fuel-section-content">
            <h4 className="section-title">Lay Out: Action Plan</h4>
            <p className="section-subtitle">
                Create the concrete action plan with all details
            </p>

            {/* Metrics Row */}
            <div className="layout-metrics-row">
                <div className="metric-field">
                    <label className="metric-label">{metricName}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                        {/* Column 1: metric value */}
                        <div className="metric-value">
                            {metricValueFormat(metricCurrentValue, metricTypeName ?? '')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                className="p-button-rounded p-button-text p-button-sm"
                                // onClick={() => setShowOverride(true)}
                                onClick={async () => {
                                    await onBeforeOverride?.();
                                    setShowOverride(true);
                                }}
                                data-tooltip-id={`${metricName}-edit`}
                            >
                                <img src={editIcon} alt="edit" style={{ width: '16px', height: '16px' }} />
                            </Button>
                            <Tooltip
                                id={`${metricName}-edit`}
                                className="rounded tooltip edit-tooltip"
                                events={['hover']}
                                place="top"
                                closeOnEsc
                                edit-tooltip
                            >
                                <div style={{ maxWidth: '380px' }}>
                                    If you believe the metric value displayed is not correct,
                                    please log an Override of the metric for annotation.
                                </div>
                            </Tooltip>
                        </div>

                        {/* Column 3: tooltip */}
                        {CAPCurrentSession?.metricCurrentValueOverride && (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <i
                                    className="pi pi-info-circle"
                                    data-tooltip-id={`${metricName}-override`}
                                    style={{
                                        position: 'absolute',
                                        cursor: 'pointer',
                                        fontSize: '30px',
                                        color: '#C0392B',
                                        backgroundColor: '#FADBD8',
                                        borderRadius: '50%',
                                        padding: '4px',
                                    }}
                                />
                                <Tooltip
                                    id={`${metricName}-override`}
                                    className="rounded tooltip override-tooltip"
                                    events={['click']}
                                    place="right-start"
                                    closeOnEsc
                                    positionStrategy="fixed"
                                    style={{ overflow: 'visible', maxHeight: 'none', zIndex: 99999 }}
                                >
                                    <div className="override-tooltip__content">
                                        <span className="override-tooltip__label">Overridden Data</span>
                                        <span className="override-tooltip__value">
                                            {metricValueFormat(
                                                CAPCurrentSession.metricCurrentValueOverride,
                                                metricTypeName ?? ''
                                            )}
                                        </span>
                                        <span className="override-tooltip__description">
                                            {CAPCurrentSession?.metricCurrentValueOverrideReason}
                                        </span>
                                    </div>
                                </Tooltip>
                            </div>
                        )}

                    </div>
                </div>
                <div className="metric-field">
                    <label htmlFor="performance-commitment" className="required-field">
                        Performance Commitment
                    </label>
                    <div className="performance-input-wrapper">
                        {/* Prefix for currency */}
                        {metricTypeId === METRIC_TYPE_CURRENCY && (
                            <span className="input-addon input-addon--prefix">$</span>
                        )}
                        <InputText
                            id="performance-commitment"
                            value={performanceCommitment}
                            onChange={(e) => handlePerformanceCommitmentChange(e.target.value)}
                            className={`performance-input ${showValidation && !performanceCommitment ? 'p-invalid' : ''}`}
                            placeholder={typeConfig.placeholder}
                            keyfilter={performanceCommitmentKeyFilter}
                        />
                        {/* Suffix for percentage, time */}
                        {typeConfig.suffix && metricTypeId !== METRIC_TYPE_CURRENCY && (
                            <span className="input-addon input-addon--suffix">{typeConfig.suffix}</span>
                        )}
                    </div>
                    <small className="field-hint">{typeConfig.hint}</small>
                </div>

                <div className="metric-field">
                    <label htmlFor="follow-up-date">
                        Follow Up Date
                    </label>
                    <Calendar
                        id="follow-up-date"
                        value={followUpDate}
                        onChange={(e) => onFollowUpDateChange(e.value as Date | null)}
                        showIcon
                        className="custom-calendar"
                        placeholder="Select"
                        minDate={minDate}
                        dateFormat="D, M dd, yy"
                    />
                </div>
            </div>

            {/* Skill, Behavior & Action Section */}
            <div className="skill-behavior-action-section">
                <div style={{ display: 'flex', gap: '100px' }}>
                    <SkillBehaviorDropdown
                        fullWidth={false}
                        skill={skillId || 0}
                        setSkill={onSkillChange}
                        behavior={behaviorId || 0}
                        setBehavior={onBehaviorChange}
                        readOnly={false}
                    />

                    <div className="col-md-4">
                        <h6 id="coaching-action-dropdown">Action</h6>
                        <Dropdown
                            id="action-dropdown"
                            value={selectedAction}
                            options={coachingActions}
                            onChange={(e) => handleActionSelect(e.value)}
                            optionLabel="coaching_action_name"
                            placeholder="Select a Coaching Action"
                            itemTemplate={coachingActionsTemplate}
                            panelClassName="custom-dropdown-panel"
                            className={`custom-dropdown ${showValidation && !actionId ? 'p-invalid' : ''}`}
                            disabled={!behaviorId}
                        />
                    </div>
                </div>
            </div>

            {/* Action Plan Form */}
            <div className="action-plan-form">
                <div className="form-field-full">
                    <DescriptionBox
                        title='Action Description'
                        responseDescription={actionDescription}
                        value={actionDescription}
                        limit={4000}
                        onChange={(val: string) => onActionDescriptionChange(val)}
                        via={CoachingTabs.CoachingActionPlan}
                        iconsTabIndex={-1}
                    />
                </div>

                <div className="form-row">
                    <div className="form-field">
                        <DescriptionBox
                            title='Success Criteria'
                            responseDescription={successCriteria}
                            value={successCriteria}
                            limit={4000}
                            onChange={(val: string) => onSuccessCriteriaChange(val)}
                            via={CoachingTabs.CoachingActionPlan}
                            iconsTabIndex={-1}
                        />
                        {/* <small className="char-count">{successCriteria.length}/1000 characters</small> */}
                    </div>

                    <div className="form-field">
                        <DescriptionBox
                            title='Notes'
                            responseDescription={notes}
                            value={notes}
                            limit={4000}
                            onChange={(val: string) => onNotesChange(val)}
                            via={CoachingTabs.CoachingActionPlan}
                            iconsTabIndex={-1}
                        />
                        {/* <small className="char-count">{notes.length}/1000 characters</small> */}
                    </div>
                </div>
            </div>
            <OverridePerformancePopup
                visible={showOverride}
                metricName={metricName}
                currentPerformance={metricCurrentValue ?? undefined}
                metricTypeId={metricTypeId}
                metricTypeName={metricTypeName}
                recognize={false}
                CAPCurrentSession={CAPCurrentSession}
                onCancel={() => setShowOverride(false)}

            />
        </div>
    );
};
